import { predict } from './ai.js';
/**
 * Clinical decision support runtime: a compiled content pack plus the evaluator.
 *
 * Runs identically on the tablet (offline) and on the server. Every output names the rule and
 * rule version that produced it and the guideline passage behind it, so a provider can see WHY,
 * and a safety reviewer can later see WHICH version said it.
 *
 * Rules marked 'draft' (not yet clinically signed off) run only when explicitly allowed, and
 * every draft output is flagged as such for the user interface.
 */
import { evaluate, parse, test } from './expr.js';
const astCache = new WeakMap();
function astOf(rule) {
    let a = astCache.get(rule);
    if (!a) {
        a = parse(rule.when);
        astCache.set(rule, a);
    }
    return a;
}
/** 'true' -> true, '12' -> 12, "'x'" or x -> 'x'. Used for classify values in the rule table. */
export function parseLiteral(s) {
    const t = s.trim();
    if (t === 'true')
        return true;
    if (t === 'false')
        return false;
    if (t === 'null' || t === '')
        return null;
    if (/^-?\d+(\.\d+)?$/.test(t))
        return Number(t);
    return t.replace(/^'(.*)'$/, '$1');
}
const fmt = (v) => {
    if (v === null)
        return '—';
    // As recorded (2.49 stays 2.49), at most two decimals for a computed value (3.3333 -> 3.33).
    if (typeof v === 'number')
        return String(Math.round(v * 100) / 100);
    return String(v);
};
/** Replace {var} placeholders with values from the context. */
export function renderMessage(template, ctx) {
    return template.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (_m, name) => fmt(ctx[name] ?? null));
}
export function evaluatePack(pack, input, opts) {
    const derived = {};
    for (const [k, v] of Object.entries(input))
        derived[k] = v === undefined ? null : v;
    if (opts.form !== undefined)
        derived.form = opts.form;
    Object.assign(derived, applyDerive(pack.derive ?? [], derived));
    const inForm = (forms) => !forms?.length || opts.form === undefined || forms.includes(opts.form);
    const titles = new Map(pack.sources.map((s) => [s.id, `${s.title} (${s.publisher}, ${s.year})`]));
    const outputs = [];
    const models = (pack.models ?? []).filter((m) => m.status !== 'retired' && (m.status !== 'draft' || opts.allowDraft) && inForm(m.forms));
    const stages = [
        ...new Set([...pack.rules.map((r) => r.stage), ...models.map((m) => m.stage)]),
    ].sort((a, b) => a - b);
    for (const stage of stages) {
        // Models of this stage run first; their outputs are visible to the rules of this stage on.
        for (const m of models) {
            if (m.stage !== stage)
                continue;
            const pred = predict(m, derived);
            if (!pred)
                continue;
            derived[m.outputs.probability] = pred.probability;
            derived[m.outputs.band] = pred.band;
            const pct = pred.probability === null ? null : Math.round(pred.probability * 100);
            outputs.push({
                ruleId: m.id,
                ruleVersion: 1,
                action: 'predict',
                severity: pred.band === 'red' ? 'danger' : pred.band === 'amber' ? 'warning' : 'info',
                title: m.title,
                message: pred.state === 'withheld'
                    ? 'Not enough of the core measurements to estimate risk.'
                    : `Estimated risk ${pct}% (${pred.band}).`,
                params: {},
                sources: [],
                draft: pred.draft,
                prediction: pred,
            });
        }
        // Within a stage every rule sees the same context: classifications apply after the stage.
        const snapshot = { ...derived };
        const writes = [];
        for (const rule of pack.rules) {
            if (rule.stage !== stage || rule.status === 'retired')
                continue;
            if (rule.status === 'draft' && !opts.allowDraft)
                continue;
            if (!inForm(rule.forms))
                continue;
            if (!test(astOf(rule), snapshot))
                continue;
            if (rule.action === 'classify') {
                const name = rule.params.var;
                if (name)
                    writes.push([name, parseLiteral(rule.params.value ?? 'true')]);
            }
            outputs.push({
                ruleId: rule.id,
                ruleVersion: rule.version,
                action: rule.action,
                severity: rule.severity,
                title: rule.title,
                message: renderMessage(rule.message, snapshot),
                params: rule.params,
                sources: rule.sources.map((s) => ({ ...s, title: titles.get(s.source) ?? s.source })),
                draft: rule.status === 'draft',
            });
        }
        for (const [k, v] of writes)
            derived[k] = v;
    }
    const visits = outputs
        .filter((o) => o.action === 'next_visit')
        .map((o) => {
        const d = o.params.days ?? '';
        return /^\d+$/.test(d) ? Number(d) : Number(derived[d]);
    })
        .filter((n) => Number.isFinite(n) && n > 0);
    return {
        outputs,
        derived,
        nextVisitDays: visits.length ? Math.min(...visits) : null,
        packId: pack.id,
        packVersion: pack.version,
    };
}
/** The context with the pack's derived variables added, in order. */
export function applyDerive(derive, ctx) {
    const out = {};
    for (const [k, v] of Object.entries(ctx))
        out[k] = v === undefined ? null : v;
    for (const d of derive)
        out[d.var] = evaluateCached(d.expr, out);
    return out;
}
function evaluateCached(expr, ctx) {
    let ast = conditionCache.get(expr);
    if (!ast) {
        ast = parse(expr);
        conditionCache.set(expr, ast);
    }
    const v = evaluate(ast, ctx);
    return typeof v === 'number' && !Number.isFinite(v) ? null : v;
}
/** Evaluate one expression against a context (used for form show_if conditions). */
const conditionCache = new Map();
export function evalCondition(expr, ctx) {
    if (!expr)
        return true;
    let ast = conditionCache.get(expr);
    if (!ast) {
        ast = parse(expr);
        conditionCache.set(expr, ast);
    }
    return test(ast, ctx);
}
export { evaluate as evaluateExpr };
//# sourceMappingURL=cds.js.map